import dayjs from "dayjs";
import { memo, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Spinner } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import images from "../../assets/images";
import { useTranslate } from "../../context/TranslationContext";
import { useLogoutMutation } from "../../redux/Apis/Auth";
import { useGetCampaignInternalListQuery } from "../../redux/Apis/Campaign";
import {
  useGetCourseExpiryNotificationsQuery,
  useGetCourseNotificationsQuery,
} from "../../redux/Apis/Notification";
import { logout } from "../../redux/Slices/Auth";
import { getThemeByKey } from "../../utils/theme";
import Button from "../button";
import Search from "../search";

const Header = memo(
  ({
    fixedHeader,
    customLogo,
    showTogglerAlways,
    showThemeToggle = false,
    themeOptions = [],
    activeTheme,
    onThemeChange,
  }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { t, lang, setLang } = useTranslate("header");
    const { isLoggedIn, user } = useSelector((state) => state.authSlice);
    const { cart } = useSelector((state) => state.generalSlice);
    const [logoutApi] = useLogoutMutation();
    const [isThemeMenuOpen, setIsThemeMenuOpen] = useState(false);
    const [isNavbarOpen, setIsNavbarOpen] = useState(false);
    const [isBootstrapDropdownOpen, setIsBootstrapDropdownOpen] =
      useState(false);
    const [isLgDownViewport, setIsLgDownViewport] = useState(() =>
      typeof window !== "undefined" ? window.innerWidth < 992 : false,
    );
    const themeMenuRef = useRef(null);
    const themeTriggerRef = useRef(null);
    const themePortalMenuRef = useRef(null);
    const headerRef = useRef(null);
    const [themeMenuPosition, setThemeMenuPosition] = useState({
      top: 0,
      left: 0,
      minWidth: 190,
    });

    const {
      data: notificationData,
      isLoading: notificationLoading,
      refetch: refetchNotification,
    } = useGetCampaignInternalListQuery({ lang }, { skip: !isLoggedIn });

    const {
      data: courseNotificationData,
      isLoading: courseNotificationLoading,
      refetch: refetchCourseNotification,
    } = useGetCourseNotificationsQuery(
      { params: { lang } },
      { skip: !isLoggedIn },
    );

    const {
      data: expiryData,
      isLoading: expiryLoading,
      refetch: refetchExpiry,
    } = useGetCourseExpiryNotificationsQuery(
      { params: { lang } },
      { skip: !isLoggedIn },
    );

    const campaignNotifications = (notificationData?.data || []).filter(
      (item) => !item.isactive,
    );
    const expiryNotifications = (expiryData?.data || []).filter(
      (item) => !item.expiry_action,
    );
    const courseNotifications = (courseNotificationData?.data || []).filter(
      (item) => !item.read,
    );
    const allNotifications = [
      ...expiryNotifications,
      ...campaignNotifications,
      ...courseNotifications,
    ];

    useEffect(() => {
      if (isLoggedIn) {
        refetchNotification();
        refetchCourseNotification();
        refetchExpiry();
      }
    }, [
      isLoggedIn,
      refetchNotification,
      refetchCourseNotification,
      refetchExpiry,
    ]);

    useEffect(() => {
      const closeOnOutsideClick = (event) => {
        const clickedInsideSwitcher = themeMenuRef.current?.contains(
          event.target,
        );
        const clickedInsidePortalMenu = themePortalMenuRef.current?.contains(
          event.target,
        );

        if (!clickedInsideSwitcher && !clickedInsidePortalMenu) {
          setIsThemeMenuOpen(false);
        }
      };

      const closeOnEscape = (event) => {
        if (event.key === "Escape") {
          setIsThemeMenuOpen(false);
        }
      };

      document.addEventListener("mousedown", closeOnOutsideClick);
      document.addEventListener("touchstart", closeOnOutsideClick);
      document.addEventListener("keydown", closeOnEscape);

      return () => {
        document.removeEventListener("mousedown", closeOnOutsideClick);
        document.removeEventListener("touchstart", closeOnOutsideClick);
        document.removeEventListener("keydown", closeOnEscape);
      };
    }, []);

    useEffect(() => {
      if (!isThemeMenuOpen) return;

      const updateThemeMenuPosition = () => {
        const triggerRect = themeTriggerRef.current?.getBoundingClientRect();
        if (!triggerRect) return;

        const menuWidth = Math.max(triggerRect.width, 190);
        const viewportPadding = 12;

        let left = triggerRect.right - menuWidth;
        if (left < viewportPadding) {
          left = viewportPadding;
        }

        const rightLimit = window.innerWidth - menuWidth - viewportPadding;
        if (left > rightLimit) {
          left = rightLimit;
        }

        setThemeMenuPosition({
          top: triggerRect.bottom + 8,
          left,
          minWidth: menuWidth,
        });
      };

      updateThemeMenuPosition();

      window.addEventListener("resize", updateThemeMenuPosition);
      window.addEventListener("scroll", updateThemeMenuPosition, true);

      return () => {
        window.removeEventListener("resize", updateThemeMenuPosition);
        window.removeEventListener("scroll", updateThemeMenuPosition, true);
      };
    }, [isThemeMenuOpen]);

    useEffect(() => {
      const syncViewport = () => {
        setIsLgDownViewport(window.innerWidth < 992);
      };

      syncViewport();
      window.addEventListener("resize", syncViewport);

      return () => {
        window.removeEventListener("resize", syncViewport);
      };
    }, []);

    useEffect(() => {
      const navbarEl = document.getElementById("navbarNav");
      if (!navbarEl) return;

      const handleShown = () => setIsNavbarOpen(true);
      const handleHidden = () => setIsNavbarOpen(false);

      navbarEl.addEventListener("shown.bs.collapse", handleShown);
      navbarEl.addEventListener("hidden.bs.collapse", handleHidden);

      return () => {
        navbarEl.removeEventListener("shown.bs.collapse", handleShown);
        navbarEl.removeEventListener("hidden.bs.collapse", handleHidden);
      };
    }, []);

    useEffect(() => {
      const onDropdownShown = (event) => {
        if (headerRef.current?.contains(event.target)) {
          setIsBootstrapDropdownOpen(true);
        }
      };

      const onDropdownHidden = () => {
        if (!headerRef.current) return;
        const hasOpenBootstrapDropdown =
          headerRef.current.querySelector(".dropdown-menu.show") !== null;
        setIsBootstrapDropdownOpen(hasOpenBootstrapDropdown);
      };

      document.addEventListener("shown.bs.dropdown", onDropdownShown);
      document.addEventListener("hidden.bs.dropdown", onDropdownHidden);

      return () => {
        document.removeEventListener("shown.bs.dropdown", onDropdownShown);
        document.removeEventListener("hidden.bs.dropdown", onDropdownHidden);
      };
    }, []);

    useEffect(() => {
      const shouldPrioritizeHeader =
        isNavbarOpen || isThemeMenuOpen || isBootstrapDropdownOpen;
      document.body.classList.toggle(
        "header-menu-open",
        shouldPrioritizeHeader,
      );

      return () => {
        document.body.classList.remove("header-menu-open");
      };
    }, [isNavbarOpen, isThemeMenuOpen, isBootstrapDropdownOpen]);

    const user_image = useMemo(() => {
      if (!user?.picture) return images.profile.placeholder;
      if (user.picture.startsWith("http")) return user.picture;
      return user.picture.startsWith("images/")
        ? `${process.env.REACT_APP_S3_BASE_URL}${user.picture}`
        : images.profile.placeholder;
    }, [user?.picture]);

    const onLogout = async () => {
      try {
        const selectedCountry = localStorage.getItem("selectedCountry");

        const { message } = await logoutApi({
          params: { lang },
        }).unwrap();

        toast.success(message || "Logged out successfully");
        dispatch(logout());

        localStorage.clear();
        sessionStorage.clear();

        if (selectedCountry) {
          localStorage.setItem("selectedCountry", selectedCountry);
        }

        setTimeout(() => navigate("/"), 700);
      } catch (error) {
        toast.error("Failed to log out. Try again.");
      }
    };

    const roleBasedMenuItems = useMemo(() => {
      if (!isLoggedIn) {
        return (
          <>
            <li className="nav-item">
              <Link to="/auth/login" className="text-nowrap">
                {t("teachOnTraineego")}
              </Link>
            </li>
            <li className="nav-item">
              <Link to="/memberships">{t("membership")}</Link>
            </li>
          </>
        );
      }

      const { role } = user;
      const isCreator = role === "creator";
      const isBoth = role === "both";

      return (
        <>
          {(isCreator || isBoth) && (
            <li className="nav-item">
              <Link to="/instructor-dashboard">{t("createCourse")}</Link>
            </li>
          )}
          <li className="nav-item">
            <Link to={isCreator ? "/my-teaching" : "/my-learning"}>
              {isCreator ? t("myTeaching") : t("myLearning")}
            </Link>
          </li>
        </>
      );
    }, [isLoggedIn, user, t]);

    const renderProfileImage = (className) => (
      <img src={user_image} alt="" className={`img-fluid ${className}`} />
    );

    const renderUserInfo = () => (
      <div className="flex-grow-1">
        <p className="p-md text-uppercase oswald mb-0">
          {user?.first_name} {user?.last_name}
        </p>
        <p className="mb-0 p-sm">{user?.email}</p>
      </div>
    );

    const renderCreatorMenuItems = () => (
      <>
        <li>
          <Link className="dropdown-item text-white" to="/my-teaching">
            {t("myTeaching")}
          </Link>
        </li>
        {["draft", "analytic", "sale", "student", "forum", "review", "pss"].map(
          (tab) => (
            <li key={tab}>
              <Link
                className="dropdown-item text-white"
                to={`/my-teaching?pillsTab=pills-${tab}`}
              >
                {t(`${tab}s`)}
              </Link>
            </li>
          ),
        )}
        <hr />
      </>
    );

    const renderLearnerMenuItems = () => (
      <>
        <li>
          <Link className="dropdown-item text-white" to="/my-learning">
            {t("myLearning")}
          </Link>
        </li>
        {["profile", "contact", "fitness", "forums", "quiz"].map((tab) => (
          <li key={tab}>
            <Link
              className="dropdown-item text-white"
              to={`/my-learning?pillsTab=pills-${tab}`}
            >
              {t(
                tab === "profile"
                  ? "myList"
                  : tab === "contact"
                    ? "wishlist"
                    : tab === "fitness"
                      ? "fitness"
                      : tab === "forums"
                        ? "forums"
                        : "quizCert",
              )}
            </Link>
          </li>
        ))}
        <hr />
      </>
    );

    const shouldShowThemeToggle =
      showThemeToggle &&
      themeOptions.length > 0 &&
      typeof onThemeChange === "function";
    const shouldRenderThemeNearToggler =
      shouldShowThemeToggle && (showTogglerAlways || isLgDownViewport);
    const isNonBlackTheme = activeTheme && activeTheme !== "current";
    const selectedTheme = useMemo(
      () => getThemeByKey(activeTheme),
      [activeTheme],
    );
    const themedHeaderBg = isNonBlackTheme ? selectedTheme?.primaryMColor : "";
    const headerControlTextClass = isNonBlackTheme ? "text-dark" : "white-text";

    const profileDropdownItems = useMemo(() => {
      if (!isLoggedIn) {
        return (
          <>
            <Button
              to="/auth/login"
              text={t("login")}
              className="text-nowrap text-white"
            />
            <Button
              text={t("signup")}
              to="/auth/signup"
              className="text-nowrap text-white"
            />
          </>
        );
      }

      const isCreator = user?.role === "creator";
      const isBoth = user?.role === "both";
      const isLearner = user?.role === "learner";

      return (
        <>
          {/* Notifications */}
          <Link to="/cart" className="d-flex">
            <img
              src={isNonBlackTheme ? images.home.black_cart : images.home.cart}
              alt=""
              className="img-fluid"
              style={{ width: "24px", height: "24px", objectFit: "contain" }}
            />
            {cart?.length > 0 && (
              <span className="cart-badge">{cart.length}</span>
            )}
          </Link>
          <div className="dropdown">
            <button
              className={`transparent-btn ${headerControlTextClass} position-relative`}
              type="button"
              data-bs-toggle="dropdown"
              aria-expanded="false"
            >
              <i className="far l-grey-text mt-1 p-lg fa-bell" />
              {allNotifications.length > 0 && (
                <span
                  className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger"
                  style={{ fontSize: "0.6rem" }}
                >
                  {allNotifications.length}
                </span>
              )}
            </button>
            <ul
              className="dropdown-menu dropdown-menu-end justify-content-center align-items-center"
              style={{ minWidth: 300 }}
            >
              <li className="dropdown-header fw-bold mb-2">
                {t("notifications")}
              </li>

              {notificationLoading ||
              courseNotificationLoading ||
              expiryLoading ? (
                <li className="dropdown-item text-center">
                  <Spinner style={{ color: "var(--primaryColor)" }} />
                </li>
              ) : allNotifications.length > 0 ? (
                <>
                  {allNotifications.slice(0, 3).map((item) => (
                    <li key={item._id} className="dropdown-item small">
                      <div className="d-flex align-items-start gap-2">
                        {!item.campaignid && item.expiry_date ? (
                          <i
                            className="fas fa-hourglass-half mt-1 text-warning"
                            title="Expiry Notification"
                          />
                        ) : item.campaignid ? (
                          <i
                            className="fas fa-bullhorn mt-1 text-danger"
                            title="Campaign Notification"
                          />
                        ) : (
                          <i
                            className="fas fa-bell mt-1 purple-text"
                            title="Course Notification"
                          />
                        )}
                        <div>
                          <div className="fw-semibold">
                            {!item.campaignid ? (
                              <span>Course: {item.title}</span>
                            ) : (
                              item.campaignid.title || "New message"
                            )}
                          </div>
                          <div className="text-muted small">
                            {dayjs(item.createdAt).format(
                              "DD MMM YYYY, hh:mm A",
                            )}
                          </div>
                        </div>
                      </div>
                    </li>
                  ))}
                  <hr className="bg-secondary my-2" />
                  <li className="text-center">
                    <Button
                      text={t("showMore")}
                      className="mx-2"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate("/notifications");
                      }}
                      style={{ width: "stretch" }}
                    />
                  </li>
                </>
              ) : (
                <img
                  src={images.modal.no_notification}
                  style={{
                    width: "300px",
                    height: "300px",
                    objectFit: "contain",
                    padding: "10px",
                  }}
                  alt="No notifications"
                />
              )}
            </ul>
          </div>

          {/* Profile */}
          <div className="dropdown user-profile-dropdown">
            <button
              className={`transparent-btn ${headerControlTextClass}`}
              type="button"
              data-bs-toggle="dropdown"
              aria-expanded="false"
            >
              {renderProfileImage("profile-pic")}
            </button>
            <ul className="dropdown-menu dropdown-menu-end white-text pb-5">
              <li className="px-3">
                <div className="d-flex align-items-center gap-3">
                  <div className="flex-shrink-0">
                    {renderProfileImage("profile-pic")}
                  </div>
                  {renderUserInfo()}
                </div>
              </li>
              <hr />
              {isCreator || isBoth
                ? renderCreatorMenuItems()
                : renderLearnerMenuItems()}
              {!isCreator && (
                <li>
                  <Link className="dropdown-item text-white" to="/cart">
                    {t("myCart")}
                  </Link>
                </li>
              )}
              {(isCreator || isBoth) && (
                <li>
                  <Link
                    className="dropdown-item text-white"
                    to="/instructor-dashboard"
                  >
                    {t("createCourse")}
                  </Link>
                </li>
              )}
              <hr />
              <li>
                <Link className="dropdown-item text-white" to="/memberships">
                  {t("membership")}
                </Link>
              </li>
              <hr />
              {(isCreator || isBoth) && (
                <li>
                  <Link
                    className="dropdown-item text-white"
                    to="/profile"
                    state={{ userProfileId: user?._id }}
                  >
                    {t("profile")}
                  </Link>
                </li>
              )}
              {isLearner && (
                <li>
                  <Link
                    className="dropdown-item text-white"
                    to="/learnerprofile"
                  >
                    {t("profile")}
                  </Link>
                </li>
              )}
              <li>
                <Link className="dropdown-item text-white" to="/profile/edit">
                  {t("editProfile")}
                </Link>
              </li>
              <hr />
              <li>
                <Link className="dropdown-item text-white" to="/contact">
                  {t("help")}
                </Link>
              </li>
              <li>
                <Link className="dropdown-item text-white" onClick={onLogout}>
                  {t("logout")}
                </Link>
              </li>
            </ul>
          </div>
        </>
      );
    }, [
      isLoggedIn,
      user,
      user_image,
      onLogout,
      allNotifications,
      notificationLoading,
      courseNotificationLoading,
      expiryLoading,
      headerControlTextClass,
      t,
    ]);

    const handleThemeSelect = (themeValue) => {
      onThemeChange(themeValue);
      setIsThemeMenuOpen(false);
    };

    const renderThemeSwitcher = (extraClass = "") => (
      <>
        <div
          className={`header-theme-switch ${isNonBlackTheme ? "theme-light" : ""} ${extraClass}`.trim()}
          style={{
            "--themeSwitchText": isNonBlackTheme ? "#101010" : "#f7f7f7",
          }}
          ref={themeMenuRef}
        >
          <button
            ref={themeTriggerRef}
            className="header-theme-trigger"
            type="button"
            aria-label="Open theme menu"
            aria-haspopup="menu"
            aria-expanded={isThemeMenuOpen}
            onClick={() => setIsThemeMenuOpen((prev) => !prev)}
          >
            <i className="fas fa-palette header-theme-icon" />
          </button>
        </div>
        {isThemeMenuOpen &&
          typeof document !== "undefined" &&
          createPortal(
            <div
              ref={themePortalMenuRef}
              className={`header-theme-menu header-theme-menu-portal ${
                isNonBlackTheme ? "theme-light" : ""
              }`.trim()}
              role="menu"
              style={{
                top: `${themeMenuPosition.top}px`,
                left: `${themeMenuPosition.left}px`,
                minWidth: `${themeMenuPosition.minWidth}px`,
                background: isNonBlackTheme ? "#fff" : "#1a1a1a",
                // color: !isNonBlackTheme ? "#101010" : "#f7f7f7",
              }}
            >
              {themeOptions.map((option) => {
                const isActive = activeTheme === option.value;
                return (
                  <button
                    key={option.value}
                    type="button"
                    role="menuitemradio"
                    aria-checked={isActive}
                    className={`header-theme-option ${isActive ? "active" : ""}`}
                    onClick={() => handleThemeSelect(option.value)}
                    style={{
                      color: isNonBlackTheme ? "#101010" : "#f7f7f7",
                    }}
                  >
                    <span
                      className="header-theme-dot"
                      style={{
                        backgroundColor: option.swatchColor,
                        borderColor:
                          option.value === "white"
                            ? "rgba(0, 0, 0, 0.2)"
                            : option.accentColor,
                      }}
                    />
                    <span className="header-theme-name">{option.label}</span>
                    {isActive && (
                      <i className="fas fa-check header-theme-check" />
                    )}
                  </button>
                );
              })}
            </div>,
            document.body,
          )}
      </>
    );

    return (
      <header
        ref={headerRef}
        className={`header-loggedin ${fixedHeader ? "fixed-header" : ""} ${isNavbarOpen ? "header-open" : ""}`}
        style={{
          "--headerThemeText": isNonBlackTheme ? "#101010" : "#ffffff",
          backgroundColor: themedHeaderBg,
        }}
      >
        <div className="container">
          <div className="row">
            <div className="col-12">
              <nav
                className={`navbar ${!showTogglerAlways && "navbar-expand-lg"}`}
              >
                {customLogo !== undefined ? (
                  customLogo ? (
                    <img
                      src={customLogo}
                      alt=""
                      style={{
                        height: "50px",
                        width: "auto",
                        objectFit: "contain",
                        borderRadius: "4px",
                      }}
                    />
                  ) : (
                    <div style={{ width: "175px", height: "62px" }} />
                  )
                ) : (
                  <Link className="navbar-brand" to="/">
                    <img
                      src="/assets/images/logo.png"
                      alt=""
                      className="img-fluid site-logo"
                    />
                  </Link>
                )}
                {shouldRenderThemeNearToggler &&
                  renderThemeSwitcher("ms-auto me-2")}
                <button
                  className={`navbar-toggler transparent-btn ${
                    showTogglerAlways ? "d-block" : ""
                  }`}
                  style={{ color: isNonBlackTheme ? "#000" : undefined }}
                  type="button"
                  data-bs-toggle="collapse"
                  data-bs-target="#navbarNav"
                  aria-controls="navbarNav"
                  aria-expanded="false"
                  aria-label="Toggle navigation"
                >
                  <i className="fas fa-bars" />
                </button>
                <div className="collapse navbar-collapse" id="navbarNav">
                  <ul
                    className={`navbar-nav mx-auto gap-xl-3 gap-2 flex-grow-1 ${!showTogglerAlways && "justify-content-lg-center align-items-lg-center"}`}
                    style={
                      showTogglerAlways && {
                        padding: "10px 20px",
                        position: "relative",
                        background: isNonBlackTheme ? "#fff" : "#1a1a1a",
                        boxShadow: "0 3px 30px #5e17eb4d",
                      }
                    }
                  >
                    <li className="nav-item">
                      <Search placeholder={t("whatLearn")} />
                    </li>
                    <li className="nav-item">
                      <Link to="/business">{t("business")}</Link>
                    </li>
                    {roleBasedMenuItems}
                    <li className="nav-item gap-xl-3 gap-2 d-flex align-items-center">
                      {profileDropdownItems}
                    </li>

                    <li className="nav-item ms-lg-3">
                      <div className="dropdown language-dropdown">
                        <button
                          className={`transparent-btn ${headerControlTextClass}`}
                          type="button"
                          data-bs-toggle="dropdown"
                          aria-expanded="false"
                        >
                          {(lang || "en").toUpperCase()}
                        </button>
                        <ul className="dropdown-menu dropdown-menu-end">
                          <li>
                            <button
                              className="dropdown-item mb-1"
                              onClick={() => setLang("en")}
                              onMouseEnter={(e) =>
                                (e.target.style.backgroundColor =
                                  "var(--primaryColor)")
                              }
                              onMouseLeave={(e) =>
                                (e.target.style.backgroundColor =
                                  lang === "en"
                                    ? "var(--primaryColor)"
                                    : "transparent")
                              }
                              style={{
                                backgroundColor:
                                  lang === "en"
                                    ? "var(--primaryColor)"
                                    : "transparent",
                                // color: lang === "en" ? "transparent" : "#fff",
                              }}
                            >
                              English
                            </button>
                          </li>
                          <li>
                            <button
                              className="dropdown-item"
                              onClick={() => setLang("es")}
                              onMouseEnter={(e) =>
                                (e.target.style.backgroundColor =
                                  "var(--primaryColor)")
                              }
                              onMouseLeave={(e) =>
                                (e.target.style.backgroundColor =
                                  lang === "es"
                                    ? "var(--primaryColor)"
                                    : "transparent")
                              }
                              style={{
                                backgroundColor:
                                  lang === "es"
                                    ? "var(--primaryColor)"
                                    : "transparent",
                                // color: lang === "es" ? "transparent" : "#fff",
                              }}
                            >
                              Español
                            </button>
                          </li>
                        </ul>
                      </div>
                    </li>
                    {!shouldRenderThemeNearToggler && shouldShowThemeToggle && (
                      <li className="nav-item ms-lg-2 d-flex align-items-center">
                        {renderThemeSwitcher()}
                      </li>
                    )}
                  </ul>
                </div>
              </nav>
            </div>
          </div>
        </div>
      </header>
    );
  },
);

export default Header;
